// @ts-check
// Settings → Sessions → Cancellations: paints the card's three windows from
// the coach's saved settings. Moved out of renderSettings() (settings.js),
// which calls it on every Settings render, so it can be tested on its own.

import { callFunction } from './api.js';

// The windows live in coaches.settings, which is what every server consumer
// (session cancel, time off, standing reservations) reads, so the card shows
// the server's copy, never the localStorage one: before 2026-08-20 the
// settings save wrote only localStorage, and a coach could see one number
// here while the server applied the default. The third value is the spec's
// default, which the server falls back to as well (cancellation-policy.js).
//
// ⚠️ THE BOXES CHANGED ON 2026-10-05 (the new session rules, approved by the
// product owner): how long a credit stays good after a client cancels with
// notice, after the coach cancels, and after the coach forgives a late cancel
// or a no-show. They replaced creditLifeDays, medicalCreditDays and
// minDaysToRebook, which still drive the clients on the old rules until
// everyone moves over, so the Save carries those along as loaded.
/** @type {Array<[string, string, number]>} */
const CANCEL_FIELDS = [
  ['s-client-cancel-extra-days', 'clientCancelExtraDays', 14],
  ['s-coach-cancel-extra-days', 'coachCancelExtraDays', 30],
  ['s-forgive-extra-days', 'forgiveExtraDays', 14],
];

// The coach's saved cancellation block from the newest load, or null while a
// load is on its way or after the newest one failed.
/** @type {Record<string, any> | null} */
let _loaded = null;

// ⚠️ WINDOWS THAT DID NOT LOAD NEVER SAVE. This card used to paint the
// defaults first and keep them when the load failed, shown as the coach's
// own. Its Save (on the "Moving a session" card below) posts all three, and
// save-coach-settings stores them whole, so one edit after a failed load
// wrote the defaults over the two windows the coach never touched. Typing
// before the load answered did the same: the stored windows were skipped to
// keep the keystrokes. Now the boxes stay empty and locked until the coach's
// own windows arrive, so nothing can be typed early and the Save tracker
// (initSettingsDirtyTracking in app.js), which lights Save only on an edit,
// cannot light it. A failed load says so, keeps Save off and clears the grey
// hint text (60, 90, 14 would read as the coach's own). Settings runs this
// on every render, so the next good load unlocks the card. The page ships
// the boxes and Save locked too (index.html), for the moments after a reload
// before the first render.
//
/**
 * The cancellation block as the Save posts it (the button is on the "Moving a
 * session" card; its handler is in app.js).
 *
 * Moved here from app.js's btn-save-sessions handler (2026-10-05). The boxes
 * are read as whole days. Anything blank or junk falls back to the spec's
 * default, which is the same number the server falls back to, so a cleared
 * box never means "zero days", which would be a credit dead on arrival.
 *
 * ⚠️ EVERYTHING ELSE IN THE BLOCK GOES BACK AS LOADED. save-coach-settings
 * stores the block whole, so a key left out is a key deleted: the three old
 * numbers the card no longer shows would fall back to the standard ones for
 * every client still on the old rules. With nothing loaded there is nothing
 * to carry, so this refuses instead of posting.
 *
 * @returns {Record<string, any>}
 */
export function readCancellationSettings() {
  if (!_loaded) throw new Error("Your cancellation settings haven't loaded yet. Reload the page and try again.");
  /** @type {Record<string, any>} */
  const out = { ..._loaded };
  for (const [id, key, dflt] of CANCEL_FIELDS) {
    const box = /** @type {HTMLInputElement | null} */ (document.getElementById(id));
    const v = parseInt(box?.value || '', 10);
    out[key] = Number.isFinite(v) && v > 0 ? v : dflt;
  }
  return out;
}

// Renders overlap (live client updates re-run renderSettings while Settings
// is open), so only the newest load's answer may paint the card: an older
// failure must not lock a card a newer load filled, nor an older answer
// unlock one a newer failure locked.
let _newestLoad = 0;

export function loadCancellationSettings() {
  const load = ++_newestLoad;
  _loaded = null;
  const boxes = CANCEL_FIELDS.map(([id]) => /** @type {HTMLInputElement | null} */ (document.getElementById(id)));
  const line = document.getElementById('s-cancellation-load-error');
  const save = /** @type {(HTMLButtonElement & { _dirtyReset?: () => void }) | null} */ (document.getElementById('btn-save-sessions'));
  for (const box of boxes) {
    if (!box) continue;
    box.value = '';
    box.disabled = true;
  }
  return callFunction('coach-settings-get').then((res) => {
    if (load !== _newestLoad) return;
    // The server always sends the settings ({} for a coach who saved none),
    // and callFunction hands back {} for an empty reply, so a reply without
    // them means they did not load.
    if (!res?.settings) throw new Error('coach-settings-get answered without the settings');
    const saved = res.settings.cancellation || {};
    _loaded = { ...saved };
    CANCEL_FIELDS.forEach(([, key, dflt], i) => {
      const box = boxes[i];
      if (!box) return;
      box.value = String(saved[key] > 0 ? saved[key] : dflt);
      box.disabled = false;
      if (box.dataset.hint) box.placeholder = box.dataset.hint;
    });
    if (line) line.hidden = true;
    // Re-snapshot so the JS-set values become the clean state (a JS .value
    // write fires no input event, so the tracker would otherwise keep the
    // empty boxes as its baseline).
    save?._dirtyReset?.();
  }).catch((e) => {
    if (load !== _newestLoad) return;
    console.warn('[cancellation-settings] load failed, so the card stays locked', e);
    for (const box of boxes) {
      if (!box) continue;
      if (box.placeholder) box.dataset.hint = box.placeholder;
      box.placeholder = '';
    }
    if (line) line.hidden = false;
    if (save) save.disabled = true;
  });
}
